// Middleware for making authenticated API calls with a Auth0 JWT Token
// Most of this is derived from an Auth0 Blog post
// https://auth0.com/blog/secure-your-react-and-redux-app-with-jwt-authentication/

import Config from '../classes/Config'
import { LS_ACCESS_TOKEN_KEY } from '../constants/local_storage'

var originalFetch = require('isomorphic-fetch')
var fetch = require('fetch-retry')(originalFetch, {
  retries: 5,
  retryDelay: 300,
  retryOn: [408, 429, 500, 502, 503, 504, 522, 524]
})

const BASE_URL = Config.ApiRoot
const EXTERNAL_API_URL = Config.ExternalApiRoot
const OPEN_API_URL = Config.OpenaiApiRoot

export function AuthException (message) {
  this.name = 'AuthException'
  this.message = message
}

export function ApiError (error, status) {
  this.name = 'ApiError'
  this.error = error
  this.status = status
}

const DefaultApiHeaders = {
  'Accept': 'application/json',
  'Content-Type': 'application/json'
}

function getAccessToken (state = {}) {
  let user = state.user || {}
  let token = null

  token = user.overrideAccessToken || user.accessToken
  if (!token) {
    console.log('No access token available in the state, reverting to local storage')
    token = localStorage.getItem(LS_ACCESS_TOKEN_KEY) || null
  }
  return token
}

/*
 * Generic function to call the api, optionally passing in the auth header.
 */
function callApi (endpoint, config = {}, baseUrl = BASE_URL) {
  let url = baseUrl + endpoint
  if (endpoint.indexOf('/api/external-public/') > -1) {
     url = EXTERNAL_API_URL + endpoint
  }
  else if (endpoint.indexOf('/ai/') > -1) {
    url = OPEN_API_URL + endpoint
  }

  // Check if openai URL, no need to retry on API failure
  const fetchOptions = endpoint.indexOf('/ai/') !== -1
    ? originalFetch
    : fetch;

  return fetchOptions(url, config)
    .then(response => {
      if (response.ok) {
        if (response.headers.get('Content-Type').includes('stream')) {
          return response.blob()
        } else {
          return response.json()
        }
      } else {
        return response.json().then(err => {
          const error = {}
          error.error = err
          error.status = response.status
          throw error
        })
      }
    }).then(json => {
      return json
    }).catch(error => {
      throw new ApiError(error.error, error.status)
    })
}

export const CALL_API = Symbol('Call API')

export default store => next => action => {
  const callAPI = action[CALL_API]

  // So the middleware doesn't get applied to every single action
  if (typeof callAPI === 'undefined') {
    return next(action)
  }

  let { endpoint, types, authenticated, config, actionAttrs, baseUrl } = callAPI
  config = config || {}
  config.headers = config.headers || { ...DefaultApiHeaders }
  // Additional attributes to put on the actions we may dispatch
  actionAttrs = actionAttrs || {}
  baseUrl = baseUrl || BASE_URL

  const [ successType, errorType ] = types
  if (authenticated) {
    let token = getAccessToken(store.getState())
    if (!token) {
      throw new AuthException('No access token available')
    }
    config.headers.Authorization = `Bearer ${token}`
  }

  // TBD do we need to pass the authenticated value back or not? The example we got this from used
  // the same api but with/without auth to get different results. I doubt we'll ever have that, but
  // we should leave some remants of support for it.
  // console.log("calling api", endpoint, store.getState())
  return callApi(endpoint, config, baseUrl).then(
    response => {
      return next({
        response,
        type: successType,
        receivedAt: Date.now(),
        ...actionAttrs
      })
    },
    error => {
      return next({
        error: error || 'There was an error.',
        type: errorType,
        receivedAt: Date.now(),
        ...actionAttrs
      })
    }
  )
}
